/**
 * useScopeEsxExport — shared "Download as ESX" save-then-export logic.
 *
 * Encapsulates the exact sequence the V1 {@link DownloadEsxButton} uses
 * (web/src/components/DownloadEsxButton.tsx) so V2 surfaces (Scope Builder,
 * Estimate Detail) can wire their own Export buttons without duplicating it:
 *
 *   - If a persisted `scopeId` is known → export it directly.
 *   - Else if `projectId` + `scopeData` are present → persist via
 *     `saveProjectScope` to obtain an id, then export that.
 *   - Then decode + trigger the browser download via `triggerEsxDownload`.
 *
 * Reuses the existing, proven backend contract `POST /esx/generate-from-scope/json`
 * (via `BackendAPI.downloadScopeEsx`). No new endpoint.
 */

import { useCallback, useState } from 'react';

import { BackendAPI } from '../lib/api';
import { triggerEsxDownload } from '../lib/esxDownload';
import type { AdapterReport } from '../types';

/** Arguments for a single export attempt. */
export interface ExportEsxArgs {
  /** Persisted scope id. When present, export is direct (no save). */
  scopeId?: string | null;
  /** Project id — required for save-then-export when `scopeId` is absent. */
  projectId?: string | null;
  /** Scope payload to persist when a save-then-export is needed. */
  scopeData?: object | null;
  /** Optional override for the ESX PROJECT_INFO/@name. */
  projectNameOverride?: string;
}

/** Result of a successful export. */
export interface ExportEsxResult {
  filename: string;
  /** The scope id the ESX was generated from (newly saved or pre-existing). */
  scopeId: string;
  report: AdapterReport;
}

/** Return shape of {@link useScopeEsxExport}. */
export interface UseScopeEsxExport {
  /** Runs an export; resolves to the result, or `null` on failure (see `error`). */
  exportEsx: (_args: ExportEsxArgs) => Promise<ExportEsxResult | null>;
  /** True while an export is in flight. */
  exporting: boolean;
  /** Human-readable message from the last failed export, else `null`. */
  error: string | null;
}

/**
 * Hook that exposes a stateful `exportEsx` action plus `exporting`/`error`.
 *
 * @returns The export action and its busy/error state.
 */
export function useScopeEsxExport(): UseScopeEsxExport {
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportEsx = useCallback(
    async (args: ExportEsxArgs): Promise<ExportEsxResult | null> => {
      setExporting(true);
      setError(null);
      try {
        let id = args.scopeId ?? null;

        // Reason: no persisted scope yet → save it to the project first so the
        // backend has a `scope_id` to generate from (mirrors DownloadEsxButton).
        if (!id && args.projectId && args.scopeData) {
          const saved = await BackendAPI.saveProjectScope(args.projectId, args.scopeData);
          id = (saved?.id as string | undefined) ?? null;
          if (!id) throw new Error('Saving the scope did not return an id.');
        }

        if (!id) {
          throw new Error('No scope available to export. Save it to a project first.');
        }

        const result = await BackendAPI.downloadScopeEsx(id, args.projectNameOverride);
        triggerEsxDownload(result.filename, result.esx_b64);
        return { filename: result.filename, scopeId: id, report: result.report };
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        return null;
      } finally {
        setExporting(false);
      }
    },
    [],
  );

  return { exportEsx, exporting, error };
}
